import React from 'react';
import { CalendarIcon, SparklesIcon, TrendingDownIcon, TrendingUpIcon, TrophyIcon, UserIcon } from 'lucide-react';
import { formatDuration, formatPercent } from '../../utils/format';
import { periodPhrases, previousLabels } from '../../utils/range';
import { teamLabel } from '../../utils/teams';
import type { CallsInsightHubState } from '../CallsInsightHub/useCallsInsightHub';
export function CallInsights({
  hub
}: {
  hub: CallsInsightHubState;
}) {
  const delta = hub.kpiTrend.totalCalls;
  const up = delta >= 0;
  const topTeam = [...hub.teamSummaries].sort((a, b) => b.stats.answerRate - a.stats.answerRate)[0];
  const low = [...hub.volume].sort((a, b) => a.outbound + a.inbound + a.missed - (b.outbound + b.inbound + b.missed))[0];
  const lowTotal = low ? low.outbound + low.inbound + low.missed : 0;
  const hourly = hub.range === 'today';
  const topUser = hub.rankedTeamRows[0];
  const items = [{
    Icon: up ? TrendingUpIcon : TrendingDownIcon,
    tone: up ? 'bg-positive/10 text-positive' : 'bg-danger/10 text-danger',
    text: <>
          Call volume {up ? 'increased' : 'decreased'} <strong className="font-semibold text-ink" data-id="element-435" data-mp="3o86z7mo8qz0e">{Math.abs(delta)}%</strong>{' '}
          {periodPhrases[hub.range]} compared to the {previousLabels[hub.range]}.
        </>
  }, topTeam && {
    Icon: TrophyIcon,
    tone: 'bg-gold-soft text-gold',
    text: <>
          <strong className="font-semibold text-ink" data-id="element-436" data-mp="ftg81bbl3hx5">{teamLabel(topTeam.team)}</strong> had the highest answer rate at{' '}
          <strong className="font-semibold text-ink" data-id="element-437" data-mp="assfumbmcv1c">{formatPercent(topTeam.stats.answerRate)}</strong>.
        </>
  }, low && {
    Icon: CalendarIcon,
    tone: 'bg-warning/10 text-warning',
    text: <>
          <strong className="font-semibold text-ink" data-id="element-438" data-mp="2qmj2f0nvieoc">{hourly ? `The ${low.label} hour` : low.label}</strong> had unusually low call
          activity ({lowTotal} calls).
        </>
  }, topUser && {
    Icon: UserIcon,
    tone: 'bg-accent-soft text-accent',
    text: <>
          <strong className="font-semibold text-ink" data-id="element-439" data-mp="2t0xzgumc5bxa">{topUser.agent.name}</strong> led{' '}
          {hub.team === 'All' ? 'the team' : teamLabel(hub.team)} with{' '}
          <strong className="font-semibold text-ink" data-id="element-440" data-mp="2e5rved6svn6l">{formatDuration(topUser.stats.talkSeconds)}</strong> of talk time.
        </>
  }].filter(Boolean) as {
    Icon: typeof UserIcon;
    tone: string;
    text: React.ReactNode;
  }[];
  return <section aria-labelledby="insights-heading" className="flex flex-col rounded-2xl border border-line bg-surface p-6 shadow-card" data-id="element-441" data-mp="2a2vgawggy97e">
      <div className="mb-2 flex items-center gap-2" data-id="element-442" data-mp="1vequvkq17jkx">
        <SparklesIcon className="h-4 w-4 text-accent-light" aria-hidden data-id="element-443" data-mp="20b7jeg157zyj" />
        <h2 id="insights-heading" className="text-base font-semibold text-ink" data-id="element-444" data-mp="1mc7s10m8x6yo">
          Call insights
        </h2>
      </div>
      <ul className="divide-y divide-line" data-id="element-445" data-mp="1qe2w2cvk9yg5">
        {items.map(({
        Icon,
        tone,
        text
      }, i) => <li key={i} className="flex items-start gap-3 py-4 last:pb-0" data-id="element-446" data-mp="17or1805fji7t" data-mpi={i}>
            <span className={`flex h-8 w-8 shrink-0 items-center justify-center rounded-full ${tone}`} data-id="element-447" data-mp="12qftbuyp3hs8">
              <Icon className="h-4 w-4" aria-hidden data-id="element-448" data-mp="1gpyzii7y4j1w" />
            </span>
            <p className="text-sm leading-relaxed text-muted" data-id="element-449" data-mp="1go4buw5aicu4">{text}</p>
          </li>)}
      </ul>
    </section>;
}